import { computed, inject, Injectable, signal } from '@angular/core';
import { lastValueFrom, map, Observable, of, tap } from 'rxjs';
import {
    AccountMovementsRestService,
    ContactMessage,
    FondsdatenResponseDto,
    HistorischeFondsdaten,
    HistorischeRueckkaufswerte,
    MessageRestService,
    Police,
    PoliceRestService,
    PoliceRoleRestService,
} from '../../_generated';
import { Beneficiary } from '../../shared/components/dialog/dialog.service';
import { PoliceViewDetail, PoliceViewList } from '../models/policy.models';
import { FileUtils } from '../utils/file.utils';
import { PolicyUtils } from '../utils/policy.utils';
import { AggregatedAnteilsguthaben, DatumEntry } from '../../features/policies/components/policy-chart/interfaces/policy-chart.interface';
import { Period } from '../../features/policies/components/policy-chart/enums/period.enum';
import ReasonEnum = ContactMessage.ReasonEnum;

@Injectable({
    providedIn: 'root',
})
export class PolicyService {
    private readonly policeRestService = inject(PoliceRestService);
    private readonly accountMovementsRestService = inject(AccountMovementsRestService);
    private readonly policeRoleRestService = inject(PoliceRoleRestService);
    private readonly messageRestService = inject(MessageRestService);

    private rawPoliciesSignal = signal<Police[]>([]);
    private policiesViewListSignal = computed(() => this.rawPoliciesSignal().map((police) => PolicyUtils.mapToPoliceViewList(police)));

    rawPolicies = this.rawPoliciesSignal;
    policies = this.policiesViewListSignal;

    // this triggers the loading of all account movements for all accounts in all policies of this partner
    async loadAccountMovements(): Promise<void> {
        try {
            await lastValueFrom(this.accountMovementsRestService.accountMovementsControllerSendMessage());
        } catch (error) {
            console.error('Error triggering account movements:', error);
        }
    }

    async getAggregatedAnteilsguthaben(policy: PoliceViewDetail, startdatum: string, enddatum: string, selectedPeriod: Period): Promise<AggregatedAnteilsguthaben[]> {
        const period = selectedPeriod === Period.THREE_YEARS || selectedPeriod === Period.MAX ? 'woche' : 'tag';
        const fondsdatenResponse: FondsdatenResponseDto = await lastValueFrom(
            this.policeRestService.getHistorischeFondsdaten(policy?.policennummer, startdatum, enddatum, period, 'body', false, { httpHeaderAccept: 'application/json' }),
        );

        if (!fondsdatenResponse.historischeFondsdaten) {
            return [];
        }

        const groupedData = new Map<string, AggregatedAnteilsguthaben>();

        fondsdatenResponse.historischeFondsdaten.forEach((fondsdaten: HistorischeFondsdaten) => {
            fondsdaten.daten.forEach((datumEntry: DatumEntry) => {
                const calculatedValue = datumEntry.wert * datumEntry.anteile;

                if (groupedData.has(datumEntry.datum)) {
                    groupedData.get(datumEntry.datum)!.value += calculatedValue;
                } else {
                    groupedData.set(datumEntry.datum, { datum: datumEntry.datum, value: calculatedValue });
                }
            });
        });

        return Array.from(groupedData.values()).sort((first, second) => new Date(first.datum).getTime() - new Date(second.datum).getTime());
    }

    async getRueckkaufsWerte(policy: PoliceViewDetail, startdatum: string, enddatum: string): Promise<HistorischeRueckkaufswerte[]> {
        const response = await lastValueFrom(
            this.policeRestService.getRueckkaufswerte(policy?.policennummer, startdatum, enddatum, 'body', false, { httpHeaderAccept: 'application/json' }),
        );
        return response.historischeRueckkaufswerte;
    }

    getAll(): Observable<PoliceViewList[]> {
        return this.policeRestService.policeControllerGetPolicen().pipe(
            tap(async (policies) => {
                this.rawPoliciesSignal.set(policies);

                if (policies.length) {
                    await this.loadAccountMovements();
                }
            }),
            map((policies) => {
                return policies.map((police) => PolicyUtils.mapToPoliceViewList(police));
            }),
        );
    }

    getPoliceRole(policennummer: string) {
        return this.policeRoleRestService.policeRoleControllerGetPoliceRollen().pipe(
            map((roles) => {
                return roles.filter((role) => role.policennummer === policennummer);
            }),
        );
    }

    changeBeguenstigungIndividual(policennummer: string, beguenstigungen: Beneficiary[] | undefined, beguenstigungText: string) {
        const payload = PolicyUtils.mapToChangeBeguenstigungIndividualPayload(policennummer, beguenstigungen || [], beguenstigungText);

        if (!beguenstigungen) {
            return of(null);
        }
        return this.messageRestService.messageControllerSendMessage([payload]);
    }

    async changeBeguenstigungStandard(policennummer: string, message: string, files: File[]) {
        const payload = await PolicyUtils.mapToChangeBeguenstigungStandardPayload(policennummer, message, files);

        return lastValueFrom(this.messageRestService.messageControllerSendMessage([payload]));
    }

    changePraemie(policennummer: string, praemie: number | undefined, reason: ReasonEnum | undefined) {
        if (!praemie || !reason) {
            return of(null);
        }
        const payload = PolicyUtils.mapToChangePraemiePayload(policennummer, praemie, reason);

        return this.messageRestService.messageControllerSendMessage([payload]);
    }

    changePaymentFrequency(policennummer: string, paymentFrequency: Police.ZahlungsartEnum | undefined) {
        if (!paymentFrequency) {
            return of(null);
        }
        const payload = PolicyUtils.mapToChangePaymentFrequencyPayload(policennummer, paymentFrequency);

        return this.messageRestService.messageControllerSendMessage([payload]);
    }

    async changePaymentMethod(policennummer: string, paymentMethod: string, iban: string, files: File[]) {
        const fileContents = await Promise.all(files.map((file) => FileUtils.fileToFileContent(file)));
        const payload = PolicyUtils.mapToChangePaymentMethodPayload(policennummer, paymentMethod, iban, fileContents);

        return lastValueFrom(this.messageRestService.messageControllerSendMessage([payload]));
    }

    async changeFunds(policennummer: string, message: string, files: File[]) {
        const fileContents = await Promise.all(files.map((file) => FileUtils.fileToFileContent(file)));

        const payload = PolicyUtils.mapToChangeFundsPayload(policennummer, message, fileContents);

        return lastValueFrom(this.messageRestService.messageControllerSendMessage([payload]));
    }

    async changeDisability(policennummer: string, message: string, files: File[]) {
        const fileContents = await Promise.all(files.map((file) => FileUtils.fileToFileContent(file)));

        const payload = PolicyUtils.mapToChangeFundsPayload(policennummer, message, fileContents);

        return lastValueFrom(this.messageRestService.messageControllerSendMessage([payload]));
    }

    async changeVollmacht(policennummer: string, message: string, files: File[]) {
        const fileContents = await Promise.all(files.map((file) => FileUtils.fileToFileContent(file)));

        const payload = PolicyUtils.mapToChangeVollmachtPayload(policennummer, message, fileContents);

        return lastValueFrom(this.messageRestService.messageControllerSendMessage([payload]));
    }

    async changeRisikozwischenversicherung(policennummer: string, message: string) {
        const payload = PolicyUtils.mapToChangeRisikozwischenversicherungPayload(policennummer, message);

        return lastValueFrom(this.messageRestService.messageControllerSendMessage([payload]));
    }

    async changeVorsorgeart(policennummer: string, message: string, files: File[]) {
        const fileContents = await Promise.all(files.map((file) => FileUtils.fileToFileContent(file)));

        const payload = PolicyUtils.mapToChangeVorsorgeartPayload(policennummer, message, fileContents);

        return lastValueFrom(this.messageRestService.messageControllerSendMessage([payload]));
    }

    async changeVersicherungsdauer(policennummer: string, message: string) {
        const payload = PolicyUtils.mapToChangeVersicherungsdauerPayload(policennummer, message);

        return lastValueFrom(this.messageRestService.messageControllerSendMessage([payload]));
    }

    async changePraemienfreistellung(policennummer: string, message: string) {
        const payload = PolicyUtils.mapToChangePraemienfreistellungPayload(policennummer, message);

        return lastValueFrom(this.messageRestService.messageControllerSendMessage([payload]));
    }

    async changeDynamik(policennummer: string, message: string) {
        const payload = PolicyUtils.mapToChangeDynamikPayload(policennummer, message);

        return lastValueFrom(this.messageRestService.messageControllerSendMessage([payload]));
    }

    async changeVersicherungsnehmer(policennummer: string, message: string, files: File[]) {
        const fileContents = await Promise.all(files.map((file) => FileUtils.fileToFileContent(file)));

        const payload = PolicyUtils.mapToChangeVersicherungsnehmerPayload(policennummer, message, fileContents);

        return lastValueFrom(this.messageRestService.messageControllerSendMessage([payload]));
    }

    getRueckkaufDocument(policennummer: string) {
        return this.policeRestService.policeControllerGetDocumentRueckkauf(policennummer).pipe(
            map((document) => {
                const blob = new Blob([document], { type: 'application/pdf' });
                FileUtils.downloadFileFromBlob(blob, 'document.pdf');
            }),
        );
    }
}
